import { FormEvent, useEffect, useState } from 'react';
import { api, Learner, Subject } from '../api';
import { useSession } from '../auth/session';
import { BrandHero, BrandLockup } from '../Brand';
import { Icon } from '../Icon';

export function SignIn({ onDone, onCancel }: { onDone: () => void; onCancel?: () => void }) {
  const { setLearner } = useSession();
  type Mode = 'signin' | 'register' | 'reset-request' | 'reset-complete';
  const [mode, setMode] = useState<Mode>('signin');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  /*
   * Reveal is per-render and never persisted. A remembered "show password"
   * preference would mean the next person at a shared machine — a library
   * terminal, a school laptop — gets the password on screen by default.
   */
  const [showPassword, setShowPassword] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [subjects, setSubjects] = useState<Subject[]>([]);
  /*
   * The honeypot's value. Always '' for a person, because nobody can see or
   * reach the field it belongs to. Anything else means the form was filled by
   * something walking the DOM.
   */
  const [contactPreference, setContactPreference] = useState('');
  const [emailReset, setEmailReset] = useState<boolean | null>(null);
  const [packIds, setPackIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Subjects are fetched before anyone has an account, so this is the one
  // catalogue call that sits outside the login wall. It returns names and
  // counts only.
  useEffect(() => { api.get<Subject[]>('/subjects').then(setSubjects).catch(() => setSubjects([])); }, []);

  // Whether this deployment sends the code or an administrator hands it over.
  // Null until it answers, so the copy below never asserts the wrong one.
  useEffect(() => {
    api.get<{ emailReset: boolean }>('/auth/config')
      .then((c) => setEmailReset(c.emailReset))
      .catch(() => setEmailReset(null));
  }, []);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (mode === 'register' && packIds.length === 0) {
      setError('Choose at least one subject to study.');
      return;
    }
    setBusy(true); setError(null); setNotice(null);
    try {
      if (mode === 'reset-request') {
        // The response is identical whether or not the address is registered,
        // so this message must not promise that an account was found.
        const res = await api.post<{ message: string }>('/auth/reset-request', { email, contactPreference });
        setNotice(res.message);
        setMode('reset-complete');
        setBusy(false);
        return;
      }
      if (mode === 'reset-complete') {
        await api.post('/auth/reset-complete', { email, code, newPassword });
        setNotice('Password updated. Sign in with your new password.');
        setPassword(''); setCode(''); setNewPassword('');
        setMode('signin');
        setBusy(false);
        return;
      }
      const learner = mode === 'signin'
        ? await api.post<Learner>('/auth/login', { email, password, contactPreference })
        : await api.post<Learner>('/auth/register', { email, password, displayName, packIds, contactPreference });
      setLearner(learner);
      onDone();
    } catch (err: any) {
      setError(err.message);
      setBusy(false);
    }
  }

  return (
    <>
      <BrandHero />
      <BrandLockup />
    <>
      <h1>{
        mode === 'signin' ? 'Sign in'
        : mode === 'register' ? 'Create an account'
        : mode === 'reset-request' ? 'Reset your password'
        : 'Enter your reset code'
      }</h1>
      <p className="muted">{
        mode === 'signin'
          ? 'This portal is for registered users. Sign in to practise, or create an account below.'
        : mode === 'register'
          ? 'Choose the subjects you are studying. You can change them later from your profile.'
        : mode === 'reset-request'
          ? emailReset === true
            ? 'Enter your email address. If it has an account, a single-use code will be sent to it.'
            : emailReset === false
              ? 'Enter your email address. An administrator issues reset codes by hand on this portal, so there is no automatic email — they will pass the code to you directly.'
              : 'Enter your email address. If it has an account, you will be given a single-use code.'
          : 'Enter the code you were given, along with a new password. Codes expire 30 minutes after they are issued.'
      }</p>

      {notice && <div className="notice" role="status">{notice}</div>}

      <form onSubmit={submit} className="card">
        {/*
          The honeypot. Positioned off-screen rather than display:none, because
          some automation skips what is explicitly hidden and fills what is
          merely out of sight. tabIndex -1 keeps it out of the keyboard path,
          which is also what makes aria-hidden legitimate here: a focusable
          element inside an aria-hidden container would be a trap for a screen
          reader user, and an unreachable one is not.

          The name is deliberately not 'website', 'url' or 'company' — those
          are names a password manager recognises and may fill, which would
          lock a real person out of their own account.
        */}
        <div className="honeypot" aria-hidden="true">
          <label htmlFor="contact-preference">Leave this field empty</label>
          <input id="contact-preference" name="contactPreference" type="text" tabIndex={-1}
                 autoComplete="off" value={contactPreference}
                 onChange={(e) => setContactPreference(e.target.value)} />
        </div>

        <label htmlFor="email">Email</label>
        <input id="email" type="email" value={email} autoComplete="email" required
               onChange={(e) => setEmail(e.target.value)}
               style={{ width: '100%', maxWidth: '22rem', font: 'inherit', padding: '0.5rem',
                        minHeight: '2.75rem', border: '1px solid var(--border-strong)', borderRadius: '8px',
                        background: 'var(--surface)', color: 'var(--ink)' }} />

        {mode === 'register' && (
          <>
            <label htmlFor="name" style={{ marginTop: '1rem' }}>Display name <span className="muted">(optional)</span></label>
            <input id="name" type="text" value={displayName} autoComplete="name"
                   onChange={(e) => setDisplayName(e.target.value)}
                   style={{ width: '100%', maxWidth: '22rem', font: 'inherit', padding: '0.5rem',
                            minHeight: '2.75rem', border: '1px solid var(--border-strong)', borderRadius: '8px',
                            background: 'var(--surface)', color: 'var(--ink)' }} />
          </>
        )}

        {mode === 'register' && (
          <fieldset style={{ marginTop: '1.25rem' }}>
            <legend>Subjects</legend>
            <p className="muted" style={{ marginTop: 0 }}>
              Pick at least one. Your catalogue and progress are scoped to what you choose.
            </p>
            {subjects.length === 0 ? (
              <p className="muted">No subjects are published yet.</p>
            ) : subjects.map((sub) => (
              <label key={sub.id} style={{ display: 'flex', gap: '0.6rem', alignItems: 'center' }}>
                <input type="checkbox" checked={packIds.includes(sub.id)}
                       onChange={(e) => setPackIds((cur) =>
                         e.target.checked ? [...cur, sub.id] : cur.filter((x) => x !== sub.id))} />
                <span>{sub.subject} <span className="muted">({sub.itemCount} questions)</span></span>
              </label>
            ))}
          </fieldset>
        )}

        {(mode === 'signin' || mode === 'register') && (
        <>
        <label htmlFor="password" style={{ marginTop: '1rem' }}>Password</label>
        <div className="password-field">
          <input id="password" type={showPassword ? 'text' : 'password'} value={password} required
                 autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
                 aria-describedby={mode === 'register' ? 'pw-help' : undefined}
                 onChange={(e) => setPassword(e.target.value)} />
          {/*
            type="button" matters: inside a form, a bare <button> submits, so a
            reveal control would post the form on every click.

            The label changes with the state rather than carrying aria-pressed
            as well. Both together make a screen reader say "Hide password,
            toggle button, pressed", which is three ways of saying one thing.
          */}
          <button type="button" className="password-reveal"
                  onClick={() => setShowPassword((v) => !v)}>
            {showPassword ? 'Hide' : 'Show'}
            <span className="visually-hidden"> password</span>
          </button>
        </div>
        {mode === 'register' && (
          <p id="pw-help" className="muted" style={{ marginTop: '0.5rem' }}>
            At least 10 characters. A passphrase of a few words is easier to remember and harder to guess.
          </p>
        )}
        {mode === 'signin' && (
          <p style={{ marginTop: '0.6rem', marginBottom: 0 }}>
            <button type="button" className="linkish"
                    onClick={() => { setMode('reset-request'); setError(null); setNotice(null); setShowPassword(false); }}>
              Forgot your password?
            </button>
          </p>
        )}
        </>
        )}

        {mode === 'reset-complete' && (
          <>
            <label htmlFor="code" style={{ marginTop: '1rem' }}>Reset code</label>
            <input id="code" type="text" value={code} required
                   /* Codes are transcribed by hand from whatever the admin used
                      to pass them on, so autocorrect and autocapitalise are off
                      and the server strips grouping before comparing. */
                   autoComplete="one-time-code" autoCapitalize="characters"
                   autoCorrect="off" spellCheck={false} inputMode="text"
                   placeholder="XXXXX-XXXXX"
                   onChange={(e) => setCode(e.target.value)}
                   style={{ width: '100%', maxWidth: '22rem', font: 'inherit', padding: '0.5rem',
                            minHeight: '2.75rem', border: '1px solid var(--border-strong)', borderRadius: '8px',
                            background: 'var(--surface)', color: 'var(--ink)', letterSpacing: '0.08em' }} />

            <label htmlFor="new-password" style={{ marginTop: '1rem' }}>New password</label>
            <div className="password-field">
              <input id="new-password" type={showPassword ? 'text' : 'password'} value={newPassword}
                     required autoComplete="new-password" aria-describedby="new-pw-help"
                     onChange={(e) => setNewPassword(e.target.value)} />
              <button type="button" className="password-reveal"
                      onClick={() => setShowPassword((v) => !v)}>
                {showPassword ? 'Hide' : 'Show'}
                <span className="visually-hidden"> password</span>
              </button>
            </div>
            <p id="new-pw-help" className="muted" style={{ marginTop: '0.5rem' }}>
              At least 10 characters. Setting a new password signs you out everywhere else.
            </p>
          </>
        )}

        {error && <div className="notice notice--warn" role="alert" style={{ marginTop: '1rem' }}>{error}</div>}

        <div className="btn-row" style={{ marginTop: '1.25rem' }}>
          <button className="btn-primary" type="submit" disabled={busy}>
            {busy ? 'Working…'
              : mode === 'signin' ? <><Icon name="log-in" />Sign in</>
              : mode === 'register' ? <><Icon name="user-plus" />Create account</>
              : mode === 'reset-request' ? 'Request a reset'
              : 'Set new password'}
          </button>

          {(mode === 'signin' || mode === 'register') && (
            <button type="button"
                    onClick={() => { setMode(mode === 'signin' ? 'register' : 'signin'); setError(null); setNotice(null); setShowPassword(false); }}>
              {mode === 'signin' ? 'Create an account instead' : 'I already have an account'}
            </button>
          )}

          {mode === 'reset-request' && (
            /* Someone who already holds a code should not have to request a
               second one just to reach the redemption form. */
            <button type="button"
                    onClick={() => { setMode('reset-complete'); setError(null); setNotice(null); }}>
              I already have a code
            </button>
          )}

          {(mode === 'reset-request' || mode === 'reset-complete') && (
            <button type="button"
                    onClick={() => { setMode('signin'); setError(null); setNotice(null); setShowPassword(false); }}>
              Back to sign in
            </button>
          )}

          {onCancel && <button type="button" onClick={onCancel}>Cancel</button>}
        </div>
      </form>
    </>
    </>
  );
}
